import React, { useState } from 'react';
import {
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  StarIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { ContactPerson, Customer } from '../../types/customer';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import SendCustomerLoginInvite from './SendCustomerLoginInvite';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface ContactPersonsManagerProps {
  contactPersons: ContactPerson[];
  onChange: (contactPersons: ContactPerson[]) => void;
  customer?: Customer; // Optional: needed for sending invitations
  onSendInvitation?: (contactPerson: ContactPerson) => void;
  readonly?: boolean;
}

const ContactPersonsManager: React.FC<ContactPersonsManagerProps> = ({
  contactPersons,
  onChange,
  customer,
  onSendInvitation,
  readonly = false,
}) => {
  const { t } = useLanguage();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<ContactPerson>({
    name: '',
    email: '',
    phone: '',
    position: '',
    fax: '',
    isPrimary: false,
  });

  const handleAdd = () => {
    setEditingIndex(contactPersons.length);
    setFormData({
      id: `contact_${Date.now()}`,
      name: '',
      email: '',
      phone: '',
      position: '',
      fax: '',
      isPrimary: contactPersons.length === 0, // First contact is primary by default
    });
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setFormData({ ...contactPersons[index] });
  };

  const checkEmailExists = async (email: string, currentIndex: number | null): Promise<boolean> => {
    try {
      // Check if email exists in users collection
      const usersQuery = query(
        collection(db, 'users'),
        where('email', '==', email.toLowerCase().trim())
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      if (!usersSnapshot.empty) {
        return true;
      }

      // Check if email exists in other contact persons (excluding current one being edited)
      const isDuplicate = contactPersons.some((cp, index) => 
        cp.email.toLowerCase().trim() === email.toLowerCase().trim() && 
        index !== currentIndex
      );

      return isDuplicate;
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error(t('customers.contactPersons.nameEmailRequired'));
      return;
    }

    // Check if email already exists
    const emailExists = await checkEmailExists(
      formData.email, 
      editingIndex === contactPersons.length ? null : editingIndex
    );

    if (emailExists) {
      toast.error('Ez az email cím már használatban van a rendszerben!');
      return;
    }

    const newContactPersons = [...contactPersons];
    
    if (editingIndex !== null) {
      if (editingIndex === contactPersons.length) {
        // Adding new contact
        newContactPersons.push(formData);
      } else {
        // Editing existing contact
        newContactPersons[editingIndex] = formData;
      }
      
      onChange(newContactPersons);
      setEditingIndex(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        position: '',
        fax: '',
        isPrimary: false,
      });
      
      toast.success(t('customers.contactPersons.saved'));
    }
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      position: '',
      fax: '',
      isPrimary: false,
    });
  };

  const handleDelete = (index: number) => {
    const newContactPersons = contactPersons.filter((_, idx) => idx !== index);
    
    // If we deleted the primary contact, make the first one primary
    if (contactPersons[index].isPrimary && newContactPersons.length > 0) {
      newContactPersons[0].isPrimary = true;
    }
    
    onChange(newContactPersons);
  };

  const handleSetPrimary = (index: number) => {
    const newContactPersons = contactPersons.map((cp, idx) => ({
      ...cp,
      isPrimary: idx === index,
    }));
    onChange(newContactPersons);
  };

  const getStatusBadge = (contact: ContactPerson) => {
    // Check if user account actually exists (has userId)
    if (contact.hasUserAccount && contact.userId) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
          <CheckCircleIcon className="h-4 w-4 mr-1" />
          {t('customers.contactPersons.registered')}
        </span>
      );
    }
    // Show pending if invitation was sent but not yet registered
    if (contact.invitationSentAt && !contact.userId) {
      return (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
          <ClockIcon className="h-4 w-4 mr-1" />
          {t('customers.contactPersons.invitationSent')}
        </span>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('customers.contactPersons.title')}
        </h3>
        {!readonly && (
          <button
            type="button"
            onClick={handleAdd}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            {t('customers.contactPersons.add')}
          </button>
        )}
      </div>

      {/* Contact Persons List */}
      {contactPersons.length === 0 && editingIndex === null ? (
        <div className="text-center py-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('customers.contactPersons.noContacts')}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {contactPersons.map((contact, index) => (
            <div
              key={contact.id || index}
              className={`p-4 rounded-lg border ${
                contact.isPrimary
                  ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20'
                  : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
              }`}
            >
              {editingIndex === index ? (
                /* Edit Form */
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('customers.contactPersons.name')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        placeholder={t('customers.contactPersons.namePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('customers.contactPersons.email')} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        placeholder={t('customers.contactPersons.emailPlaceholder')}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('customers.contactPersons.phone')}
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        placeholder={t('customers.contactPersons.phonePlaceholder')}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('customers.contactPersons.position')}
                      </label>
                      <input
                        type="text"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                        placeholder={t('customers.contactPersons.positionPlaceholder')}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.isPrimary || false}
                        onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {t('customers.contactPersons.setPrimary')}
                      </span>
                    </label>
                  </div>
                  <div className="flex justify-end space-x-2 pt-2">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700"
                    >
                      {t('common.save')}
                    </button>
                  </div>
                </div>
              ) : (
                /* Display Mode */
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        {contact.name}
                      </h4>
                      {contact.isPrimary && (
                        <StarIconSolid className="h-4 w-4 text-yellow-500 flex-shrink-0" title={t('customers.contactPersons.primaryContact')} />
                      )}
                      {getStatusBadge(contact)}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{contact.position}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                      <span>📧 {contact.email}</span>
                      {contact.phone && <span>📞 {contact.phone}</span>}
                    </div>
                  </div>
                  {!readonly && (
                    <div className="flex items-center space-x-2 ml-4">
                      {!contact.isPrimary && (
                        <button
                          type="button"
                          onClick={() => handleSetPrimary(index)}
                          className="text-gray-400 hover:text-yellow-500"
                          title={t('customers.contactPersons.setPrimary')}
                        >
                          <StarIcon className="h-4 w-4" />
                        </button>
                      )}
                      {customer && (
                        <SendCustomerLoginInvite
                          customer={customer}
                          contactPerson={contact}
                          onSuccess={() => {
                            // Refresh contact persons list after successful invitation
                            if (onSendInvitation) {
                              onSendInvitation(contact);
                            }
                          }}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => handleEdit(index)}
                        className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                        title={t('common.edit')}
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(index)}
                        className="text-red-600 hover:text-red-700 dark:text-red-400"
                        title={t('common.delete')}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Add New Form */}
          {editingIndex === contactPersons.length && (
            <div className="p-4 rounded-lg border border-dashed border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('customers.contactPersons.name')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                      placeholder={t('customers.contactPersons.namePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('customers.contactPersons.email')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                      placeholder={t('customers.contactPersons.emailPlaceholder')}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('customers.contactPersons.phone')}
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                      placeholder={t('customers.contactPersons.phonePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('customers.contactPersons.position')}
                    </label>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                      placeholder={t('customers.contactPersons.positionPlaceholder')}
                    />
                  </div>
                </div>
                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isPrimary || false}
                      onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {t('customers.contactPersons.setPrimary')}
                    </span>
                  </label>
                </div>
                <div className="flex justify-end space-x-2 pt-2">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700"
                  >
                    {t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactPersonsManager;
